'use client';

import { useCallback, useState } from 'react';

import { formatPriceInput } from '@/lib/utils/formatPriceInput';
import { removeCommas } from '@/lib/utils/removeCommas';
import { validateNumberInput } from '@/lib/utils/validateNumberInput';

interface I_UseNumberInputValidationOptions {
  initialValue?: string;
  onValueChange?: (value: string) => void;
  onValidValueChange?: (value: number) => void;
  decimalPlaces?: number;
}

export function useNumberInputValidation({
  initialValue = '',
  onValueChange,
  onValidValueChange,
  decimalPlaces = 2
}: I_UseNumberInputValidationOptions = {}) {
  const [value, setValue] = useState<string>(initialValue);
  const [isValid, setIsValid] = useState<boolean>(true);

  const handleInputChange = useCallback(
    (inputValue: string) => {
      const formattedValue = formatPriceInput(inputValue, decimalPlaces);
      const valid = validateNumberInput(formattedValue);

      setValue(formattedValue);
      setIsValid(valid);
      onValueChange?.(formattedValue);

      if (valid && formattedValue !== '') {
        const numericValue = parseFloat(removeCommas(formattedValue));
        onValidValueChange?.(numericValue);
      }
    },
    [onValueChange, onValidValueChange, decimalPlaces]
  );

  const reset = useCallback(() => {
    setValue('');
    setIsValid(true);
  }, []);

  const handleFocusOut = useCallback(() => {
    // Do NOT reformat on blur, to respect user input
    // The value is already formatted by handleInputChange
  }, []);

  return {
    value,
    isValid,
    handleInputChange,
    handleFocusOut,
    reset
  };
}
